import { type FC, type ReactNode, useState, useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';
import dibsCss from 'dibs-css';
import { trackEvent, eventNameConstants, interactionTypeConstants } from 'dibs-tracking';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { BuyerIncentivesBannerCompactLazy } from 'dibs-buyer-incentives/exports/BuyerIncentivesBannerCompactLazy.ts';

import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext.tsx';
import { PdpSharedDibsPromiseModalLazy } from '../../PdpSharedModals/PdpSharedDibsPromiseModal/PdpSharedDibsPromiseModalLazy.tsx';
import { useIsSellerBrandingV2Variant } from '../../helpers/sellerBrandingV2TestHelpers.ts';
import { getPurchaseProtectionLink } from '../../helpers/purchaseProtectionLink.tsx';

import { type PdpSharedBuyerIncentivesCompact_user$key } from './__generated__/PdpSharedBuyerIncentivesCompact_user.graphql.ts';

const userFragment = graphql`
    fragment PdpSharedBuyerIncentivesCompact_user on User {
        ...PdpSharedDibsPromiseModalLazy_user
    }
`;

export const PdpSharedBuyerIncentivesCompact: FC<{
    user: PdpSharedBuyerIncentivesCompact_user$key | null | undefined;
    wrapperClassName?: string;
}> = ({ user: userRef, wrapperClassName }) => {
    const user = useFragment(userFragment, userRef);
    const { isMobile } = useServerVarsContext();
    const isSellerBrandingV2Variant = useIsSellerBrandingV2Variant();

    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleOpenModal = useCallback(() => {
        setIsModalOpen(true);
        trackEvent({
            eventName: eventNameConstants.EVENT_PRODUCT_INTERACTION,
            interaction_type: interactionTypeConstants.INTERACTION_TYPE_EBI_DETAILS,
            step_interaction_name: 'EBI seller info details click',
        });
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
    }, []);

    const getLink = (chunk: ReactNode): ReactNode => {
        if (isSellerBrandingV2Variant) {
            return getPurchaseProtectionLink('EBI seller info details click')(chunk);
        }

        return <Link onClick={handleOpenModal}>{chunk}</Link>;
    };

    return (
        <>
            <BuyerIncentivesBannerCompactLazy
                isSingleItem
                getLink={getLink}
                isSellerBrandingV2Variant={isSellerBrandingV2Variant}
                wrapperClassName={wrapperClassName}
                contentClassName={classnames({
                    [dibsCss.itemsCenter]: !isMobile && !isSellerBrandingV2Variant,
                })}
            />
            {!isSellerBrandingV2Variant && (
                <PdpSharedDibsPromiseModalLazy
                    user={user}
                    isOpen={isModalOpen}
                    onClose={handleCloseModal}
                />
            )}
        </>
    );
};
